import { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTheme } from 'styled-components';
import Avatar from '../components/Avatar';
import Loader from '../components/Loader';
import {
  Section,
  SectionItem,
  SectionLink,
  SectionName,
} from '../components/Chrome';
import {
  Drawer,
  DrawerExit,
  DrawerLink,
  DrawerList,
  DrawerTrigger,
} from '../components/Drawer';
import { A } from '../components/Markdown';
import AboutSpaceMemberModal from '../modals/AboutSpaceMember';
import DeleteSpaceMemberModal from '../modals/DeleteSpaceMember';
import TransferOwnershipModal from '../modals/TransferOwnership';
import EditRoleModal from '../modals/EditRole';
import Icon from '../components/Icon';
import { SPACE, SPACE_MEMBERS, VIEWER } from '../utilities/queries';
import { useSpaceContext } from '../contexts/space';

const SpaceSettingsMembersPage = () => {
  const params = useParams();
  const theme = useTheme();
  const { spaceVars } = useSpaceContext();
  const { loading: loadingSpace, data: dataSpace } = useQuery(SPACE, {
    variables: { slug: params.s, ...spaceVars },
  });
  const { loading: loadingSpaceMembers, data: dataSpaceMembers } = useQuery(
    SPACE_MEMBERS,
    {
      variables: { slug: params.s },
    }
  );
  const { loading: loadingViewer, data: dataViewer } = useQuery(VIEWER);

  const [isAboutSpaceMemberModalOpen, setIsAboutSpaceMemberModalOpen] =
    useState(false);
  const [isDeleteSpaceMemberModalOpen, setIsDeleteSpaceMemberModalOpen] =
    useState(false);
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);
  const [isTransferOwnershipModalOpen, setIsTransferOwnershipModalOpen] =
    useState(false);

  // Used to pass user data to action modals
  const [member, setMember] = useState();

  // Tracks the space member the viewer is interacting with
  const listRef = useRef();
  const [trackUser, setTrackUser] = useState();

  return (
    <>
      <Section>
        {loadingSpace || loadingSpaceMembers || loadingViewer ? (
          <Loader />
        ) : (
          <>
            <Helmet>
              <title>Members</title>
            </Helmet>
            <SectionName isSecondLevel>Members</SectionName>
            {dataSpace &&
            dataSpaceMembers &&
            dataSpace.space.planLevel === 0 &&
            dataSpaceMembers.space.members.length >= 2 ? (
              <SectionItem>
                <span>
                  Max members for your{' '}
                  <A href={`/s/${params.s}/settings/plan`}>plan</A>.
                </span>
              </SectionItem>
            ) : (
              <SectionLink to="invite-link">
                <span>Invite link</span>
                <Icon name="arrow-right" />
              </SectionLink>
            )}
            <SectionItem isBlock />
            {dataSpaceMembers?.space?.members?.map((member) => (
              <Drawer key={member.user.id} onClose={() => setTrackUser(null)}>
                <DrawerTrigger onClick={() => setTrackUser(member.user.id)}>
                  <SectionLink
                    as="button"
                    isActive={trackUser === member.user.id}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gridGap: theme.space[3],
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Avatar src={member.user.avatarUrl} isSmall />
                      <div>
                        {member.user.name} (
                        {member.user.id === dataSpace.space.ownerId
                          ? 'owner'
                          : member.role}
                        )
                      </div>
                    </div>
                    <Icon name="more" />
                  </SectionLink>
                </DrawerTrigger>
                <DrawerList ref={listRef}>
                  <DrawerExit />
                  <DrawerLink
                    as="button"
                    onClick={() => {
                      setMember(member);
                      setIsAboutSpaceMemberModalOpen(true);
                    }}
                    dontRunOnClose
                  >
                    About user
                  </DrawerLink>
                  {member.user.id !== dataSpace.space.ownerId &&
                    member.user.id !== dataViewer.viewer.id && (
                      <>
                        <DrawerLink
                          as="button"
                          onClick={() => {
                            setMember(member);
                            setIsDeleteSpaceMemberModalOpen(true);
                          }}
                          dontRunOnClose
                        >
                          Remove user
                        </DrawerLink>
                        <DrawerLink
                          as="button"
                          onClick={() => {
                            setMember(member);
                            setIsEditRoleModalOpen(true);
                          }}
                          dontRunOnClose
                        >
                          Change user role
                        </DrawerLink>
                      </>
                    )}
                  {dataViewer.viewer.id === dataSpace.space.ownerId &&
                    member.user.id !== dataSpace.space.ownerId && (
                      <DrawerLink
                        as="button"
                        onClick={() => {
                          setMember(member);
                          setIsTransferOwnershipModalOpen(true);
                        }}
                        dontRunOnClose
                      >
                        Transfer ownership
                      </DrawerLink>
                    )}
                </DrawerList>
              </Drawer>
            ))}
          </>
        )}
      </Section>
      {isAboutSpaceMemberModalOpen && (
        <AboutSpaceMemberModal
          member={member}
          onClose={() => {
            setTrackUser(null);
            setIsAboutSpaceMemberModalOpen(false);
          }}
        />
      )}
      {isDeleteSpaceMemberModalOpen && (
        <DeleteSpaceMemberModal
          member={member}
          onClose={() => {
            setTrackUser(null);
            setIsDeleteSpaceMemberModalOpen(false);
          }}
        />
      )}
      {isEditRoleModalOpen && (
        <EditRoleModal
          member={member}
          onClose={() => {
            setTrackUser(null);
            setIsEditRoleModalOpen(false);
          }}
        />
      )}
      {isTransferOwnershipModalOpen && (
        <TransferOwnershipModal
          member={member}
          onClose={() => {
            setTrackUser(null);
            setIsTransferOwnershipModalOpen(false);
          }}
        />
      )}
      {
        // prettier-ignore
        dataSpace &&
        dataSpaceMembers &&
        // If on the Free plan and less than 2 users
        ((dataSpace.space.planLevel === 0 && dataSpaceMembers.space.members.length < 2) ||
        // If on the Plus plan and less than 10 users OR
        (dataSpace.space.planLevel === 1 && dataSpaceMembers.space.members.length < 10) ||
        // If on the Internal or Unknown (greater than 2 or less than 0) plan
        dataSpace.space.planLevel === 2 || dataSpace.space.planLevel > 2 || dataSpace.space.planLevel < 0) ? (
          <Outlet />
        ) : null
      }
    </>
  );
};

export default SpaceSettingsMembersPage;
